import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import notify from "devextreme/ui/notify";
import { TagBox } from "devextreme-react/tag-box";
import { Button } from "devextreme-react/button";
import data from "../assets/apiRoutes.json";

const ExportDashboard = (props) => {
    const accessToken = props.oidc.user.access_token;

    const [dashboards, setDashboards] = useState([]);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await axios.get(
                    `${window._env_.REACT_APP_APIS}/${data.DB.SearchDashboard}`,
                    {
                        params: { dashboardName: "" },
                        headers: {
                            "Content-Type": "application/json",
                            access_token: accessToken,
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );
                const rows = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
                setDashboards(rows);
            } catch (err) {
                console.error("Failed to load dashboards", err);
                notify({ message: "Failed to load dashboards", width: 280 }, "error", 3000);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [accessToken]);

    const onExport = async () => {
        if (selected.length === 0) {
            notify({ message: "Select at least one dashboard to export.", width: 320 }, "warning", 2500);
            return;
        }
        setExporting(true);
        try {
            const res = await axios.post(
                `${window._env_.REACT_APP_APIS}/${data.DB.ExportDashboards}`,
                { dashboardRowIds: selected },
                {
                    responseType: "blob",
                    headers: {
                        "Content-Type": "application/json",
                        access_token: accessToken,
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );

            // Pull filename from Content-Disposition if available; otherwise generate.
            let fileName = `argos-dashboards-${new Date()
                .toISOString()
                .replace(/[:.]/g, "-")}.json`;
            const cd = res.headers?.["content-disposition"];
            if (cd) {
                const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
                if (match && match[1]) fileName = decodeURIComponent(match[1]);
            }

            const blob = new Blob([res.data], { type: "application/json" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            notify({ message: `Exported ${selected.length} dashboard(s).`, width: 320 }, "success", 3000);
        } catch (err) {
            console.error("Export failed", err);
            notify({ message: "Export failed. See console for details.", width: 320 }, "error", 4000);
        } finally {
            setExporting(false);
        }
    };

    return (
        <div style={{ padding: 20, maxWidth: 720 }}>
            <h6 style={{ color: window._env_.Color2, fontWeight: 700, marginBottom: 20 }}>
                Export Dashboard
            </h6>

            <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                    Dashboards to export (from current facility)
                </label>
                <TagBox
                    dataSource={dashboards}
                    valueExpr="rowid"
                    displayExpr="layoutname"
                    value={selected}
                    onValueChanged={(e) => setSelected(e.value || [])}
                    searchEnabled={true}
                    showSelectionControls={true}
                    placeholder={loading ? "Loading..." : "Select dashboards"}
                    multiline={true}
                    stylingMode="outlined"
                />
            </div>

            <Button
                text={exporting ? "Exporting..." : "Export"}
                type="default"
                disabled={exporting || loading || selected.length === 0}
                onClick={onExport}
            />

            <p style={{ marginTop: 24, fontSize: 12, color: "#666" }}>
                A JSON file containing the selected dashboards will be downloaded to your computer.
                Use the Import Dashboard screen to load it into another facility.
            </p>
        </div>
    );
};

function mapStateToProps(state) {
    return { oidc: state.oidc };
}

export default connect(mapStateToProps)(ExportDashboard);
